import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "CompanyCreditReport",
  components: { PageControl, PageComponent, SectionSheet },
  props: {
    sheet_elements: {
      type: String,
      default: '.sheet',
      required: false
    },
    customer_credit_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      export_options: {
        business_information: false,
        credit_score: false,
        principals: false,
        raw_json: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    safe_customer_credit_data() {
      return this.customer_credit_data || {};
    },
    // Flattened helpers for easier access
    cv4bcb01s() {
      return this.safe_customer_credit_data.CV4BCB01S || {};
    },
    cv4bcb01() {
      return this.cv4bcb01s.CV4BCB01 || {};
    },
    credit_vision_vars() {
      const arr =
        (this.cv4bcb01.CreditVisionVariable || {}).CV4BVariableCB01 || [];
      const obj = {};
      arr.forEach((v) => {
        if (v.VariableName) obj[v.VariableName] = v.VariableValue;
      });
      return obj;
    },
    header() {
      return this.safe_customer_credit_data.Header || {};
    },
    names() {
      return this.safe_customer_credit_data.Names || {};
    },
    principals() {
      const principals = this.safe_customer_credit_data.Principals?.Principal;
      if (!principals) return [];
      return Array.isArray(principals) ? principals : [principals];
    },
    registered_principals() {
      const principals = this.safe_customer_credit_data.RegisteredPrincipals?.RegisteredPrincipal;
      if (!principals) return [];
      // If only one principal, it's an object, not array
      return Array.isArray(principals) ? principals : [principals];
    },
    registration_details() {
      return this.safe_customer_credit_data.RegistrationDetails || {};
    },
    registration_details_extended() {
      return this.safe_customer_credit_data.RegistrationDetailsExtended || {};
    },
    company_search() {
      return this.safe_customer_credit_data.CompanySearch || {};
    },
    first_response() {
      return this.safe_customer_credit_data.FirstResponse || {};
    },
    report_reference() {
      return this.first_response.TicketNumber || "";
    },
    score_date() {
      return this.credit_vision_vars.SCOREDATE || "";
    },
    generated_date() {
      return this.score_date;
    },
    business_name() {
      return (
        this.names.BusinessName || this.company_search.BusinessName || "N/A"
      );
    },
    registration_number() {
      return (
        this.header.RegistrationNumber ||
        this.company_search.RegNo ||
        this.cv4bcb01.RegNumber ||
        "N/A"
      );
    },
    registration_date() {
      // Prefer RegistrationDetails, fallback to CompanySearch
      const d =
        this.registration_details.RegistrationDate ||
        this.company_search.RegistrationDate;
      if (!d) return "N/A";
      if (d.length === 8)
        return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
      return d;
    },
    registration_status() {
      return (
        this.registration_details.RegistrationStatusDesc ||
        this.company_search.RegStatus ||
        "N/A"
      );
    },
    it_number() {
      return this.header.ITNumber || this.credit_vision_vars.ITNO || "N/A";
    },
    vat_number() {
      return (
        this.header.VATNumbers?.string?.[0] ||
        this.credit_vision_vars.VATNO ||
        "N/A"
      );
    },
    tax_number() {
      return this.header.TaxNumber || "N/A";
    },
    duns_number() {
      return this.header.DunsNumber || this.first_response.DunsNumber || "N/A";
    },
    business_type() {
      return (
        this.header.BusinessType ||
        this.registration_details.CompanyTypeDesc ||
        "N/A"
      );
    },
    financial_year_end() {
      // Convert 02 to February, etc.
      const map = {
        "01": "January",
        "02": "February",
        "03": "March",
        "04": "April",
        "05": "May",
        "06": "June",
        "07": "July",
        "08": "August",
        "09": "September",
        10: "October",
        11: "November",
        12: "December",
      };
      const code = this.registration_details.FinancialYearEnd;
      return map[code] || code || "N/A";
    },
    physical_address() {
      return (
        this.header.PhysicalAddress ||
        this.company_search.PhysicalAddress ||
        "N/A"
      );
    },
    postal_address() {
      return (
        this.header.PostalAddress || this.company_search.PostalAddress || "N/A"
      );
    },
    website() {
      return this.header.Website || "N/A";
    },    
    auditors() {
      return (
        this.registration_details.Auditors ||
        "No auditor information on record."
      );
    },
    auditor_addresses() {
      return (
        (this.registration_details_extended &&
         this.registration_details_extended.AuditorBusinessAddresses &&
         this.registration_details_extended.AuditorBusinessAddresses.AuditorBusinessAddress) || 
        []
      );
    },
    // Credit scores
    overall_score() {
      return this.credit_vision_vars.CV4BSCORE || "N/A";
    },
    overall_band() {
      return this.credit_vision_vars.CV4BBAND || "N/A";
    },
    liquidation_score() {
      return this.credit_vision_vars.LIQSCORE || "N/A";
    },
    judgment_score() {
      return this.credit_vision_vars.JDGSCORE || "N/A";
    },
    delinquency_score() {
      return this.credit_vision_vars.DLQSCORE || "N/A";
    },
    principal_score() {
      return this.credit_vision_vars.PRPSCORE || "N/A";
    },
    // Score factors
    liquidation_factors() {
      return [
        this.credit_vision_vars.LIQAARC1,
        this.credit_vision_vars.LIQAARC2,
        this.credit_vision_vars.LIQAARC3,
        this.credit_vision_vars.LIQAARC4,
      ].filter(Boolean);
    },
    judgment_factors() {
      return [
        this.credit_vision_vars.JDGAARC1,
        this.credit_vision_vars.JDGAARC2,
        this.credit_vision_vars.JDGAARC3,
        this.credit_vision_vars.JDGAARC4,
      ].filter(Boolean);
    },
    delinquency_factors() {
      return [
        this.credit_vision_vars.DLQAARC1,
        this.credit_vision_vars.DLQAARC2,
        this.credit_vision_vars.DLQAARC3,
        this.credit_vision_vars.DLQAARC4,
      ].filter(Boolean);
    },
    principal_factors() {
      return [
        this.credit_vision_vars.PRPAARC1,
        this.credit_vision_vars.PRPAARC2,
        this.credit_vision_vars.PRPAARC3,
      ].filter(Boolean);
    },
    // Principal summary
    number_of_active_principals() {
      return this.principals.length;
    },
    // Principal table helpers
    principal_table_rows() {
      return this.principals.map((p) => ({
        name: [p.Forename1, p.Forename2, p.Forename3, p.Surname]
          .filter(Boolean)
          .join(" "),
        position: p.Position,
        id_number: p.IDNumber,
        date_started: this.formatDate(p.DateStarted),
      }));
    },    // Registered principal history including resigned directors from PrincipalArchivesP5
    principal_history_rows() {
      // Get current principals from RegisteredPrincipals
      const active_rows = this.registered_principals.map((rp) => ({
        name: `${rp.Initials} ${rp.Surname}`,
        position: "Director",
        date_started: this.formatDate(rp.RPDate),
        date_resigned: "",
        status: "Active",
        id_number: rp.IDNumber
      }));
      
      // Get resigned principals from PrincipalArchivesP5 if available
      let principal_archives = this.safe_customer_credit_data.PrincipalArchivesP5 || {};
      // If PrincipalArchiveP5 is a single object, convert to array
      let archived_principals = principal_archives.PrincipalArchiveP5 || [];
      if (archived_principals && !Array.isArray(archived_principals)) {
        archived_principals = [archived_principals];
      }
      
      const resigned_rows = archived_principals.map((ap) => ({
        name: `${ap.Initials} ${ap.Surname}`,
        position: ap.Position || "Director",
        date_started: this.formatDate(ap.DateStarted),
        date_resigned: this.formatDate(ap.DateResigned),
        status: ap.Status || "Resigned",
        id_number: ap.IDNumber
      }));
      
      // Combine and sort by date_started (newest first)
      return [...active_rows, ...resigned_rows].sort((a, b) => {
        // Try to compare dates, fall back to string comparison
        const date_a = a.date_started !== "N/A" ? new Date(a.date_started) : new Date(0);
        const date_b = b.date_started !== "N/A" ? new Date(b.date_started) : new Date(0);
        return date_b - date_a;
      });
    },
  
    // Paginate principal history into chunks of 30
    principal_history_pages() {
      const all_history = this.principal_history_rows;
      const page_size = 25;
      const pages = [];
      
      for (let i = 0; i < all_history.length; i += page_size) {
        pages.push({
          page_number: Math.floor(i / page_size) + 1,
          total_pages: Math.ceil(all_history.length / page_size),
          rows: all_history.slice(i, i + page_size),
          start_index: i + 1,
          end_index: Math.min(i + page_size, all_history.length),
          total_count: all_history.length
        });
      }
      
      return pages;
    },
    has_selected_tables() {
      return Object.values(this.export_options).some((selected) => selected);
    },
  },
  methods: {
    format_factor(factor) {
      if (!factor) return "N/A";
      const formatted = factor.charAt(0).toUpperCase() + factor.slice(1).toLowerCase();
      return formatted;
    },
    formatDate(date) {
      return format_date(date);
    },
    percent(val) {
      if (!val || isNaN(val)) return "0%";
      return `${Math.min(
        100,
        Math.round((parseInt(val, 10) / 1000) * 1000) / 10
      )}%`;
    },
    risk_label(score) {
      if (score === "N/A") return { label: "N/A", class: "" };
      const n = parseInt(score, 10);
      if (n >= 800) return { label: "Low Risk", class: "risk-low" };
      if (n >= 600) return { label: "Medium Risk", class: "risk-medium" };
      return { label: "High Risk", class: "risk-high" };
    },
    mounted_graphs() {
      // Define color mapping and score ranges for each metric
      const scoreConfigs = {
        overall: {
          id: 'chart-overall-score',
          value: this.overall_score,
          label: 'Overall',
          ranges: [
            { min: 800, max: 999, color: '#28a745', label: 'Low Risk' },
            { min: 600, max: 799, color: '#ffc107', label: 'Medium Risk' },
            { min: 1,   max: 599, color: '#dc3545', label: 'High Risk' }
          ]
        },
        liquidation: {
          id: 'chart-liquidation-score',
          value: this.liquidation_score,
          label: 'Trade',
          ranges: [
            { min: 771, max: 999, color: '#28a745', label: 'less than 0.8%' },
            { min: 702, max: 770, color: '#ffc107', label: '0.8% to 7%' },
            { min: 1,   max: 701, color: '#dc3545', label: 'greater than 7%' }
          ]
        },
        judgment: {
          id: 'chart-judgment-score',
          value: this.judgment_score,
          label: 'Judgement',
          ranges: [
            { min: 771, max: 999, color: '#28a745', label: 'less than 0.8%' },
            { min: 702, max: 770, color: '#ffc107', label: '0.8% to 7%' },
            { min: 1,   max: 701, color: '#dc3545', label: 'greater than 7%' }
          ]
        },
        delinquency: {
          id: 'chart-delinquency-score',
          value: this.delinquency_score,
          label: 'Delinquency',
          ranges: [
            { min: 776, max: 999, color: '#28a745', label: 'less than 11.8%' },
            { min: 758, max: 775, color: '#ffc107', label: '11.8% to 19.6%' },
            { min: 1,   max: 757, color: '#dc3545', label: 'greater than 19.6%' }
          ]
        },
        principal: {
          id: 'chart-principal-score',
          value: this.principal_score,
          label: 'Principal',
          ranges: [
            { min: 800, max: 999, color: '#28a745', label: 'Low Risk' },
            { min: 600, max: 799, color: '#ffc107', label: 'Medium Risk' },
            { min: 1,   max: 599, color: '#dc3545', label: 'High Risk' }
          ]
        }
      };

      Object.values(scoreConfigs).forEach(metric => {
        const raw = parseInt(metric.value, 10) || 0;
        const pct = Math.min(100, Math.round((raw / 1000) * 100));
        // Find the color for the current score range
        let color = '#008FFB';
        for (const range of metric.ranges) {
          if (raw >= range.min && raw <= range.max) {
            color = range.color;
            break;
          }
        }
        const opts = {
          series: [pct],
          chart: { 
            type: 'radialBar', 
            height: 250 
          },
          plotOptions: {
            radialBar: {
              startAngle: -135,
              endAngle: 135,
              hollow: { size: `60%` },
              dataLabels: {
                name: { show: true, offsetY: -10, formatter: () => metric.label },
                value: { show: true, offsetY: 10, formatter: () => `${raw}` }
              }
            }
          },
          colors: [color]
        };
        const el = document.querySelector(`${this.sheet_elements || ""} #${metric.id}`);
        if (el && window.ApexCharts) {
          new window.ApexCharts(el, opts).render();
        }
      });
    },
    close_modal() {
      $("#export-modal-" + this.report_reference).modal("hide");
    },

    show_export_modal() {
      this.export_options.business_information = true;
      this.export_options.credit_score = true;
      this.export_options.principals = true;
      this.export_options.raw_json = false;

      $("#export-modal-" + this.report_reference).modal("show");
    },

    async load_script(src) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async ensure_libraries_loaded() {
      try {
        if (this.export_format === "csv" && !this.libraries_loaded.papa_parse) {
          if (typeof Papa === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.4.1/papaparse.min.js"
            );
          }
          this.libraries_loaded.papa_parse = true;
        }

        if (this.export_format === "excel" && !this.libraries_loaded.sheetjs) {
          if (typeof XLSX === "undefined") {
            await this.load_script(
              "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"
            );
          }
          this.libraries_loaded.sheetjs = true;
        }
      } catch (error) {
        throw new Error("Failed to load export libraries");
      }
    },

    get_current_date_string() {
      const current_date = new Date();
      return current_date.toISOString().split("T")[0];
    },

    prepare_business_information_data() {
      return [
        {
          "Report Reference": this.report_reference,
          "Business Name": this.business_name,
          "Registration Number": this.registration_number,
          "Registration Date": this.registration_date,
          "Registration Status": this.registration_status,
          "Business Type": this.business_type,
          "IT Number": this.it_number,
          "VAT Number": this.vat_number,
          "Tax Number": this.tax_number,
          "DUNS Number": this.duns_number,
          "Financial Year End": this.financial_year_end,
          "Physical Address": this.physical_address,
          "Postal Address": this.postal_address,
          "Website": this.website,
          "Score Date": this.score_date,
          "Generated Date": this.generated_date,
        },
      ];
    },

    prepare_credit_score_data() {
      return [
        {
          "Overall Score": this.overall_score,
          "Overall Band": this.overall_band,
          "Liquidation Score": this.liquidation_score,
          "Judgement Score": this.judgment_score,
          "Delinquency Score": this.delinquency_score,
          "Principal Score": this.principal_score,
          "Liquidation Factors": this.liquidation_factors.join(", "),
          "Judgement Factors": this.judgment_factors.join(", "),
          "Delinquency Factors": this.delinquency_factors.join(", "),
          "Principal Factors": this.principal_factors.join(", "),
        },
      ];
    },    prepare_principals_data() {
      // Convert principal_table_rows and principal_history_rows to data for export
      const current_principals = this.principal_table_rows.map(principal => ({
        "Principal Name": principal.name,
        "Position": principal.position,
        "ID Number": principal.id_number,
        "Date Started": principal.date_started,
        "Status": "Active"
      }));

      // Get all principal history rows (unpaginated for export)
      const principal_history = this.principal_history_rows.map(principal => ({
        "Principal Name": principal.name,
        "Position": principal.position,
        "ID Number": principal.id_number,
        "Date Started": principal.date_started,
        "Date Resigned": principal.date_resigned,
        "Status": principal.status
      }));

      return [...current_principals, ...principal_history];
    },

    prepare_raw_json_data() {
      return {
        report_metadata: {
          report_reference: this.report_reference,
          generated_date: this.generated_date,
          export_timestamp: new Date().toISOString(),
        },
        customer_credit_data: this.safe_customer_credit_data,
      };
    },

    async export_csv() {
      const Papa = window.Papa;
      const current_date = this.get_current_date_string();
      const tables_to_export = [];

      if (this.export_options.business_information) {
        const business_data = this.prepare_business_information_data();
        const csv_content = Papa.unparse(business_data);
        tables_to_export.push({
          name: `CreditReport_BusinessInformation_${current_date}.csv`,
          content: csv_content,
        });
      }

      if (this.export_options.credit_score) {
        const credit_data = this.prepare_credit_score_data();
        const csv_content = Papa.unparse(credit_data);
        tables_to_export.push({
          name: `CreditReport_CreditScore_${current_date}.csv`,
          content: csv_content,
        });
      }

      if (this.export_options.principals) {
        const principals_data = this.prepare_principals_data();
        const csv_content = Papa.unparse(principals_data);
        tables_to_export.push({
          name: `CreditReport_Principals_${current_date}.csv`,
          content: csv_content,
        });
      }

      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        const json_content = JSON.stringify(json_data, null, 2);
        tables_to_export.push({
          name: `CreditReport_RawData_${current_date}.json`,
          content: json_content,
          type: "application/json",
        });
      }

      tables_to_export.forEach((table) => {
        const blob = new Blob([table.content], {
          type: table.type || "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", table.name);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },

    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = this.get_current_date_string();
      const workbook = XLSX.utils.book_new();

      if (this.export_options.business_information) {
        const business_data = this.prepare_business_information_data();
        const worksheet = XLSX.utils.json_to_sheet(business_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Business Information");
      }

      if (this.export_options.credit_score) {
        const credit_data = this.prepare_credit_score_data();
        const worksheet = XLSX.utils.json_to_sheet(credit_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Credit Score");
      }

      if (this.export_options.principals) {
        const principals_data = this.prepare_principals_data();
        const worksheet = XLSX.utils.json_to_sheet(principals_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Principals");
      }

      if (this.export_options.raw_json) {
        const json_data = this.prepare_raw_json_data();
        // For Excel, we'll create a simple table with the JSON as text
        const json_table = [
          {
            "JSON Data": JSON.stringify(json_data, null, 2),
          },
        ];
        const worksheet = XLSX.utils.json_to_sheet(json_table);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Raw JSON Data");
      }

      const filename = `CreditReport_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },

    async export_json() {
      const current_date = this.get_current_date_string();
      const export_data = {};

      if (this.export_options.business_information) {
        export_data.business_information = this.prepare_business_information_data()[0];
      }

      if (this.export_options.credit_score) {
        export_data.credit_score = this.prepare_credit_score_data()[0];
      }

      if (this.export_options.principals) {
        export_data.principals = this.prepare_principals_data();
      }

      if (this.export_options.raw_json) {
        export_data.raw_data = this.prepare_raw_json_data();
      }

      const json_content = JSON.stringify(export_data, null, 2);
      const blob = new Blob([json_content], {
        type: "application/json;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `CreditReport_${current_date}.json`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async export_data() {
      try {
        await this.ensure_libraries_loaded();
        if (this.export_format === "csv") {
          await this.export_csv();
        } else if (this.export_format === "excel") {
          await this.export_excel();
        } else if (this.export_format === "json") {
          await this.export_json();
        }
        $("#export-modal-" + this.report_reference).modal("hide");
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      }
    },
  },
  async mounted() {
    const load_script = src =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          // already injected
          const check = () => {
            // ← wait for ApexCharts, not jspdf
            if (src.includes('apexcharts') && window.ApexCharts) {
              resolve();
            } else {
              setTimeout(check, 50);
            }
          };
          return check();
        }
        const s = document.createElement('script');
        s.src = src;
        s.async = false;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });

    // only loading ApexCharts here
    await load_script('https://cdn.jsdelivr.net/npm/apexcharts');
    // now guaranteed window.ApexCharts → draw
    this.mounted_graphs();
  }
};
